import { useEffect, useState } from 'react';

// Pulse animation component
function PulseWrapper({ children, onClick, className = "", style = {}, disabled = false, hasBeenClicked = false }) {
    const handleKeyDown = (e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            className={`pulse-wrapper ${className} ${onClick ? 'cursor-pointer' : ''}`}
            style={{
                position: 'relative',
                display: 'inline-block',
                ...style
            }}
        >
            {children}
            <style>{`
                .pulse-wrapper::before {
                    content: "";
                    position: absolute;
                    top: -4px;
                    right: -8px;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    transform: scale(0);
                    animation: ${hasBeenClicked ? 'none' : 'pulse-circle 2s infinite'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                    pointer-events: none;
                    z-index: 10;
                }

                .pulse-wrapper::after {
                    content: "";
                    position: absolute;
                    top: -4px;
                    right: -8px;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    transform: scale(0);
                    animation: ${hasBeenClicked ? 'none' : 'pulse-circle 2s infinite 0.6s'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                    pointer-events: none;
                    z-index: 10;
                }

                @keyframes pulse-circle {
                    0% {
                        transform: scale(0);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.5);
                        opacity: 0.7;
                    }
                    100% {
                        transform: scale(2.5);
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
}

// Sparkle animation component
function SparkleWrapper({ children, onClick, className = "", style = {}, disabled = false, hasBeenClicked = false }) {
    const handleClick = (e) => {
        if (!disabled && onClick) {
            onClick(e);
        }
    };

    const handleKeyDown = (e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={`sparkle-wrapper ${className} ${onClick ? 'cursor-pointer' : ''}`}
            style={{
                position: 'relative',
                display: 'inline-block',
                ...style
            }}
        >
            {children}
            <div className="third-sparkle"></div>
            <style>{`
                .sparkle-wrapper {
                    position: relative;
                }

                .sparkle-wrapper::before,
                .sparkle-wrapper::after {
                    content: "";
                    position: absolute;
                    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23f08080"><path d="M12 0l2.5 7.5L22 10l-7.5 2.5L12 20l-2.5-7.5L2 10l7.5-2.5z"/></svg>');
                    background-size: contain;
                    background-repeat: no-repeat;
                    pointer-events: none;
                    z-index: 10;
                    transition: opacity 0.3s ease-out;
                }

                .sparkle-wrapper::before {
                    top: -8px;
                    right: -8px;
                    width: 14px;
                    height: 14px;
                    animation: ${hasBeenClicked ? 'none' : 'sparkle-fade 2s infinite'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                }

                .sparkle-wrapper::after {
                    bottom: -6px;
                    left: -6px;
                    width: 12px;
                    height: 12px;
                    animation: ${hasBeenClicked ? 'none' : 'sparkle-fade 2s infinite 1s'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                }

                .sparkle-wrapper .third-sparkle {
                    content: "";
                    position: absolute;
                    top: -4px;
                    left: -4px;
                    width: 10px;
                    height: 10px;
                    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23f08080"><path d="M12 0l2.5 7.5L22 10l-7.5 2.5L12 20l-2.5-7.5L2 10l7.5-2.5z"/></svg>');
                    background-size: contain;
                    background-repeat: no-repeat;
                    animation: ${hasBeenClicked ? 'none' : 'sparkle-fade 2s infinite 0.5s'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                    transition: opacity 0.3s ease-out;
                    pointer-events: none;
                    z-index: 10;
                }

                @keyframes sparkle-fade {
                    0%, 100% {
                        opacity: 0;
                        transform: scale(0.6) rotate(0deg);
                    }
                    50% {
                        opacity: 1;
                        transform: scale(1.4) rotate(180deg);
                    }
                }
            `}</style>
        </div>
    );
}

// Border pulse animation component
function BorderPulseWrapper({ children, onClick, className = "", style = {}, disabled = false, hasBeenClicked = false }) {
    const handleKeyDown = (e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            className={`border-pulse-wrapper ${className} ${onClick ? 'cursor-pointer' : ''}`}
            style={{
                position: 'relative',
                display: 'inline-block',
                ...style
            }}
        >
            {children}
            <style>{`
                .border-pulse-wrapper::before {
                    content: "";
                    position: absolute;
                    top: -2px;
                    left: -2px;
                    right: -2px;
                    bottom: -2px;
                    border: 2px solid #f08080;
                    border-radius: 6px;
                    animation: ${hasBeenClicked ? 'none' : 'border-pulse 2s infinite'};
                    opacity: ${hasBeenClicked ? '0' : '1'};
                    pointer-events: none;
                    z-index: 1;
                }

                @keyframes border-pulse {
                    0% {
                        transform: scale(1);
                        opacity: 0;
                    }
                    15% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    40% {
                        transform: scale(1.02);
                        opacity: 0.8;
                    }
                    70% {
                        transform: scale(1.06);
                        opacity: 0.4;
                    }
                    100% {
                        transform: scale(1.12);
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
}

// Image focus animation component - works with overflow hidden
function ImageFocusWrapper({ children, onClick, className = "", style = {}, disabled = false, hasBeenClicked = false }) {
    const handleKeyDown = (e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ") && onClick) {
            e.preventDefault();
            onClick(e);
        }
    };

    return (
        <div
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            className={`image-focus-wrapper ${className} ${onClick ? 'cursor-pointer' : ''}`}
            style={{
                position: 'relative',
                display: 'inline-block',
                ...style
            }}
        >
            {children}
            <style>{`
                .image-focus-wrapper::before {
                    content: "";
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    animation: ${hasBeenClicked ? 'none' : 'image-focus 2s infinite'};
                    pointer-events: none;
                    z-index: 10;
                }

                .image-focus-wrapper::after {
                    content: "";
                    position: absolute;
                    top: 6px;
                    right: 6px;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    background: hsl(var(--primary));
                    animation: ${hasBeenClicked ? 'none' : 'image-focus 2s infinite 0.6s'};
                    pointer-events: none;
                    z-index: 10;
                }

                @keyframes image-focus {
                    0% {
                        transform: scale(0);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.5);
                        opacity: 0.7;
                    }
                    100% {
                        transform: scale(2.5);
                        opacity: 0;
                    }
                }
            `}</style>
        </div>
    );
}

// Main component that renders the appropriate wrapper based on type
// "sparkle" | "pulse" | "border" | "image-focus"
function EngagingWrapper({
    children,
    type = "sparkle",
    onClick,
    className = "",
    style = {},
    disabled = false,
    elementId = null, // Unique ID for this element
    oneTime = false, // Whether to stop animation after first click
    storageType = "local" // "session" or "local"
}) {
    const [hasBeenClicked, setHasBeenClicked] = useState(false)

    // Check if this element has been clicked before
    useEffect(() => {
        if (elementId && oneTime) {
            const storage = storageType === "local" ? localStorage : sessionStorage;
            const clickedElements = JSON.parse(storage.getItem('engaging-wrapper-clicked') || '[]');
            if (clickedElements.includes(elementId)) {
                setHasBeenClicked(true);
            }
        }
    }, [elementId, oneTime, storageType]);

    // Handle click and mark as clicked if oneTime is true
    const handleClick = (e) => {
        if (onClick) {
            onClick(e)
        }

        if (elementId && oneTime && !hasBeenClicked) {
            try {
                setHasBeenClicked(true)
                const storage = storageType === "local" ? localStorage : sessionStorage;
                const clickedElements = JSON.parse(storage.getItem('engaging-wrapper-clicked') || '[]');
                if (!clickedElements.includes(elementId)) {
                    clickedElements.push(elementId);
                    storage.setItem('engaging-wrapper-clicked', JSON.stringify(clickedElements));
                }
            } catch (error) {
                setHasBeenClicked(true)
                console.log("error with engaging wrapper")
                console.error(error)
            }

        }
    };

    // If it's been clicked and oneTime is true, just return children without wrapper
    if (hasBeenClicked && oneTime) {
        return children;
    }

    const props = {
        children,
        onClick: handleClick,
        className,
        style,
        disabled,
        hasBeenClicked
    }

    switch (type) {
        case "pulse":
            return <PulseWrapper {...props} />
        case "border":
            return <BorderPulseWrapper {...props} />
        case "image-focus":
            return <ImageFocusWrapper {...props} />
        case "sparkle":
        default:
            return <SparkleWrapper {...props} />
    }
}

export default EngagingWrapper;
